import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { notifyError } from './utils';
import { getAPIUrl } from './clientHelpers';
import { getAccessJwtToken } from './amplify';
import { deleteDataFromLocalStorage } from './localStorage';

type AxiosInstanceWithOrigin = typeof instance & { origin: typeof axios };

const instance = axios.create({
  baseURL: getAPIUrl(),
});
instance.defaults.headers.common['Content-Type'] = 'application/json';
instance.defaults.headers.post['Content-Type'] = 'application/json';

const setAuthHeader = (token: string) => {
  if (!token) return;
  instance.defaults.headers.common['Authorization'] = `Bearer ${token}`;
};

const getAuthHeader = () => {
  return instance.defaults.headers.common['Authorization'];
};

const setInterceptors = () => {
  instance.interceptors.request.use(async (request: InternalAxiosRequestConfig) => {
    request.headers.set('Authorization', `Bearer ${await getAccessJwtToken()}`);
    return request;
  });
  instance.interceptors.response.use(
    (response: AxiosResponse) => {
      return {
        success: true,
        status: response.status,
        data: response.data,
      } as unknown as AxiosResponse;
    },
    async (error: unknown) => {
      const axiosError = error as AxiosError;
      const originalRequest = axiosError.config;
      const resp = axiosError.response || axiosError;
      const data = axiosError.response?.data || resp;
      const status = axiosError.response?.status ?? 500;

      if (status >= 400 && status < 500) {
        if (status === 401) {
          const responseData = axiosError.response?.data as { message?: string } | undefined;
          if (responseData?.message === 'Token Expired') {
            const newToken = await getAccessJwtToken();
            if (newToken && originalRequest) {
              originalRequest.headers.set('Authorization', `Bearer ${newToken}`);
              return instance(originalRequest);
            }
          } else {
            deleteDataFromLocalStorage();
            window.location.href = '/';
          }
        }
        notifyError(data as string);
      }

      return Promise.reject({ success: false, status, data });
    },
  );
};

(instance as AxiosInstanceWithOrigin).origin = axios;
export { setAuthHeader, setInterceptors, getAuthHeader };
export default instance;
